import React, { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Tooltip } from 'antd'

import {
  DownloadIcon,
  EyeIcon,
  DeleteIcon,
  ListIcon,
  GalleryIcon,
} from '@/shared/ui/icons'
import { useRequest } from '@/shared/api'
import { Toast } from '@/shared/ui/organisms/toast'
import { getUsersTimeByZoneLTS } from '@/shared/lib/formatter'
import { Spinner } from '@/shared/ui/atoms/spinner' 
import { DeleteConfirm } from '@/shared/ui/molecules/delete-confirm'
import GroupImageViewer from './image-group-viewer'
import { PanelWrap } from './sub-info-style'
import { useDownloadFile } from './use-download-file'
import { isImageExtension, isDocumentExtension, getFileExtension } from './utils'
import type { SubInfoProps, AttachmentFile, UploaderDispatchAction } from './types'

function getFileName(attached: AttachmentFile): string {
  if (attached.uploadedFile) {
    return (
      attached.uploadedFile.fileOriginalName ??
      attached.uploadedFile.name?.substring(14) ??
      ''
    )
  }
  return (
    attached.fileOriginalName ??
    attached.name?.substring(14) ??
    ''
  )
}

function getDisplayName(attached: AttachmentFile): string {
  return (
    attached.fileOriginalName ??
    attached.uploadedFile?.fileOriginalName ??
    attached.name ??
    attached.uploadedFile?.name ??
    ''
  )
}

function getFilePath(attached: AttachmentFile): string {
  if (attached.uploadedFile?.path) {
    return attached.uploadedFile.path
  }
  return attached.path ?? ''
}

function isImageFile(attached: AttachmentFile): boolean {
  const ext = attached.extension ?? ''
  const uploadedExt = attached.uploadedFile?.extension ?? ''
  return ext.includes('image/') || uploadedExt.includes('image/')
}

function SubInfo({
  isLoading,
  attachments,
  dispatch,
  galView,
  data,
  deletePermission,
  t,
  item,
  setItem,
  setDrawerOpen,
  setGalView,
  width,
}: SubInfoProps) {
  const { request } = useRequest()
  const [imagePath, setImagePath] = useState('')
  const [fileOrgName, setFileOrgName] = useState('')
  const [file, setFile] = useState<AttachmentFile | Record<string, never>>({})
  const [photoIndex, setPhotoIndex] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const { isDownloading, downloadFile, activeFileId, activeFileIndex } =
    useDownloadFile()

  const imageList =
    attachments && attachments.length > 0
      ? attachments.filter((f) => isImageExtension(getFileExtension(f)))
      : []

  const docList =
    attachments && attachments.length > 0
      ? attachments.filter((f) => isDocumentExtension(getFileExtension(f)))
      : []

  const deleteAttachment = useMutation<any, any, any>({
    mutationFn: (id: string) => {
      return request({
        url: `/v1/attachments/${id}/delete`,
        method: 'DELETE',
      })
    },
  })

  const onConfirm = useCallback(
    (attached: AttachmentFile) => {
      const id = attached.id ?? attached.attachmentId
      if (!id) return

      deleteAttachment.mutate(id, {
        onSuccess: () => {
          const newData = data?.attachments?.filter(
            (doc) =>
              doc.attachmentId !== attached.attachmentId ||
              doc.id !== attached.id
          )

          if (typeof (data as any)?.setDocs === 'function') {
            (data as any).setDocs(newData, true)
          } else {
            dispatch({
              type: 'setDocs',
              payload: newData,
              own: 'true',
            })
          }

          Toast({
            type: 'success',
            message: 'Files successfully deleted !',
          })
        },
        onError: (err: Error) => {
          Toast({
            type: 'error',
            message: err.message,
          })
        },
      })
    },
    [data, deleteAttachment, dispatch]
  )

  const onCancel = useCallback((msg?: string) => {
    Toast({
      type: 'error',
      message: msg ?? 'Action cancelled!',
    })
  }, [])

  const handleShowFilesInLine = useCallback(
    (e: React.MouseEvent, attached: AttachmentFile, index: number) => {
      const fileName = getFileName(attached)
      const filePath = getFilePath(attached)

      setFileOrgName(fileName)
      setImagePath(filePath)
      e.stopPropagation()
      setPhotoIndex(index)
      setIsOpen(true)
    },
    []
  )

  const handleDownload = useCallback(
    (
      e: React.MouseEvent,
      name: string,
      path: string,
      fileId?: string | number | null,
      index?: number | null
    ) => {
      e.stopPropagation()
      downloadFile(name, path, fileId, index)
    },
    [downloadFile]
  )

  const icons = [
    <div
      key="download-icon"
      className="w-10.75 h-12.25 p-2.25 flex justify-center items-center"
      onClick={(e) => {
        e.stopPropagation()
        handleDownload(e, fileOrgName, imagePath, null, photoIndex)
      }}
    >
      {isDownloading && photoIndex === activeFileIndex ? (
        <Spinner />
      ) : (
        <DownloadIcon
          lineheight="30px"
          width="27px"
          height="27px"
          color="#fff"
          id="img-download-antd"
        />
      )}
    </div>,
  ]

  const onFileClick = useCallback((f: AttachmentFile) => {
    setFile(f)
  }, [])

  const closeShowFiles = useCallback(() => {
    setFile({})
  }, [])

  return (
    <>
      <div className="flex items-center gap-4  bg-[#f3f3f3] px-6 py-2">
        <Tooltip
          zIndex={9999}
          placement="top"
          title={t('list_view')}
          color="blue"
        >
          <div
            style={{
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: !galView
                ? 'var(--primary-background-color)'
                : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <ListIcon
              width="16px"
              height="16px"
              color={!galView ? '#fff' : '#374151'}
              margin="0"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setItem(item)
                setGalView(false)
              }}
            />
          </div>
        </Tooltip>
        <Tooltip
          zIndex={9999}
          placement="top"
          title={t('gallery_view')}
          color="blue"
        >
          <div
            style={{
              padding: '4px',
              borderRadius: '4px',
              backgroundColor: galView
                ? 'var(--primary-background-color)'
                : 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <GalleryIcon
              width="16px"
              height="16px"
              color={galView ? '#fff' : '#374151'}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                setGalView(true)
              }}
            />
          </div>
        </Tooltip>
      </div>

      {isOpen && imageList.length > 0 ? (
        <GroupImageViewer
          imagesList={[...imageList, ...docList]}
          dispatch={dispatch}
        />
      ) : null}

      {isLoading ? (
        <Spinner />
      ) : attachments && attachments.length > 0 ? (
        <PanelWrap style={{ width: width ?? 'inherit' }}>
          {galView ? (
            <div className="files-box">
              <GroupImageViewer
                imagesList={
                  attachments && attachments.length > 0
                    ? attachments
                    : [...imageList, ...docList]
                }
                dispatch={dispatch}
              />
            </div>
          ) : (
            <>
              {attachments &&
                attachments.map((attached, index) => (
                  <PanelWrap.Table
                    className="flex"
                    key={attached.id ?? attached.attachmentId ?? index}
                  >
                    <div
                      className="name-box"
                      title={
                        attached.name ?? attached.uploadedFile?.name ?? undefined
                      }
                    >
                      <div className="key">{t('name')}:</div>
                      <div className="value turncated">
                        {getDisplayName(attached)}
                      </div>
                    </div>
                    <div
                      className="author-box"
                      title={attached.uploadedBy ?? undefined}
                    >
                      <div className="key">{t('uploaded_by')}:</div>
                      <div className="value turncated">
                        {attached.uploadedBy}
                      </div>
                    </div>
                    <div
                      className="date-box"
                      title={
                        attached.createdAt
                          ? getUsersTimeByZoneLTS(attached.createdAt, 'L LT')
                          : undefined
                      }
                    >
                      <div className="key">{t('uploaded_date')}:</div>
                      <div className="value turncated">
                        {attached.createdAt
                          ? getUsersTimeByZoneLTS(attached.createdAt, 'L LT')
                          : ''}
                      </div>
                    </div>
                    <div className="action-box">
                      {isImageFile(attached) ? (
                        <EyeIcon
                          onClick={(e: React.MouseEvent) =>
                            handleShowFilesInLine(e, attached, index)
                          }
                        />
                      ) : (
                        <EyeIcon
                          onClick={() => onFileClick(attached)}
                        />
                      )}

                      {isDownloading &&
                      (attached.id ?? attached.attachmentId) ===
                        activeFileId ? (
                        <Spinner />
                      ) : (
                        <DownloadIcon
                          title="Download file"
                          onClick={(e: React.MouseEvent) => {
                            handleDownload(
                              e,
                              attached.fileOriginalName ?? attached.name ?? '',
                              attached.path ?? '',
                              attached.id ?? attached.attachmentId
                            )
                          }}
                        />
                      )}

                      {deletePermission && (
                        <DeleteConfirm
                          title={` ${
                            attached.name ??
                            attached.uploadedFile?.fileOriginalName ??
                            ''
                          }`}
                          onConfirm={() => onConfirm(attached)}
                          onCancel={onCancel}
                        >
                          <DeleteIcon />
                        </DeleteConfirm>
                      )}
                    </div>
                  </PanelWrap.Table>
                ))}
            </>
          )}
        </PanelWrap>
      ) : (
        <div className="w-full mb-3 px-6 py-4 text-sm text-[#999]">
          {t('no_records') || 'No records!'}
        </div>
      )}
    </>
  )
}

export default React.memo(SubInfo)
